import { Combobox, createListCollection } from '@ark-ui/react/combobox';
import { Dialog } from '@ark-ui/react/dialog';
import { Portal } from '@ark-ui/react/portal';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckIcon, ChevronsUpDownIcon, Trash2Icon, XIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from '../../contexts';
import type { Order, Project, WBS } from '../../data/databaseSchema';
import type { CreateFavouriteRequest } from '../../services/favouriteService';
import { favouriteService } from '../../services/favouriteService';
import { orderService } from '../../services/orderService';
import { projectService } from '../../services/projectService';
import {
  type OrderInfo,
  type ProjectInfo,
  type ProjectLogEntry,
  type TaskInfo,
  timeEntryService,
} from '../../services/timeEntryService';
import { wbsService } from '../../services/wbsService';
import { buttonTheme, dialogTheme, mergeStyles } from '../../theme/theme';
import { generateId } from '../../utils/generateId';

interface ProjectHoursFormData {
  id: string;
  project_info: ProjectInfo;
  order_info: OrderInfo;
  task_info: TaskInfo;
  hours_logged: number;
  description: string;
  date_of_year: string;
  status: string;
  work_type: string | null;
  billable: boolean;
}

interface ProjectHoursDialogProps {
  selectedDate: Date;
  isOpen: boolean;
  onClose: () => void;
  onSave: (entry: ProjectLogEntry) => void;
  onEntrySaved?: () => void;
  entry?: ProjectLogEntry | null;
  isEdit?: boolean;
  actualHours?: number;
  plannedHours?: number;
  projectLoggedHours: ProjectLogEntry[];
  isBridge?: boolean;
}

export const ProjectHoursDialog: React.FC<ProjectHoursDialogProps> = ({
  selectedDate,
  isOpen,
  onClose,
  onSave,
  onEntrySaved,
  entry,
  isEdit = false,
  actualHours,
  plannedHours,
  projectLoggedHours,
  isBridge = false,
}) => {
  const { employeeId } = useSession();

  const [formData, setFormData] = useState<ProjectHoursFormData>({
    id: generateId(),
    project_info: {
      project_id: '',
      project_number: '',
      project_name: '',
    },
    order_info: {
      order_id: '',
      order_number: '',
      comments: '',
    },
    task_info: {
      task_id: '',
      task_code: '',
      task_name: '',
      billable: false,
    },
    hours_logged: 0,
    description: '',
    date_of_year: selectedDate.toISOString(),
    status: 'draft',
    work_type: null,
    billable: false,
  });

  const totalProjectHours = useMemo(() => {
    const existingHours =
      projectLoggedHours?.reduce((acc, projectLogEntry) => {
        if (isEdit && projectLogEntry.id === entry?.id) {
          return acc;
        }
        return acc + projectLogEntry.hours_logged;
      }, 0) ?? 0;

    const formHours = formData.hours_logged > 0 ? formData.hours_logged : 0;
    return existingHours + formHours;
  }, [projectLoggedHours, isEdit, entry?.id, formData.hours_logged]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedFavouriteId, setSelectedFavouriteId] = useState<string>('');
  const [isDeletingFavourite, setIsDeletingFavourite] = useState<string | null>(null);
  const [showArchivedWarning, setShowArchivedWarning] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');

  // Store original overflow value to persist across unmounts (for unmountOnExit)
  const originalOverflowRef = useRef<string | null>(null);
  const prevProjectIdRef = useRef<string>('');
  const prevProjectTypeRef = useRef<string | null>(null);

  // TanStack Query to fetch projects
  const {
    data: projectsData,
    isLoading: isLoadingProjects,
    error: projectsError,
  } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectService.getProjects(),
    enabled: isOpen, // Only fetch when dialog is open
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
  });

  // TanStack Query to fetch orders by project
  const {
    data: ordersData,
    isLoading: isLoadingOrders,
    error: ordersError,
  } = useQuery({
    queryKey: ['orders', formData.project_info.project_id],
    queryFn: () => orderService.getOrdersByProject(formData.project_info.project_id),
    enabled: isOpen && formData.project_info.project_id !== '', // Only fetch when dialog is open and project is selected
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
  });

  // TanStack Query to fetch WBS items
  const {
    data: wbsData,
    isLoading: isLoadingWbs,
    error: wbsError,
  } = useQuery({
    queryKey: ['wbs'],
    queryFn: () => wbsService.getWbs(),
    enabled: isOpen, // Only fetch when dialog is open
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
  });

  // TanStack Query to fetch favourites
  const {
    data: favourites,
    isLoading: isLoadingFavourites,
    error: favouritesError,
  } = useQuery({
    queryKey: ['favourites'],
    queryFn: () => favouriteService.getFavourites(),
    enabled: isOpen && !isEdit, // Only fetch when dialog is open and not in edit mode
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
  });

  const queryClient = useQueryClient();

  const createFavouriteMutation = useMutation({
    mutationFn: (data: CreateFavouriteRequest) => favouriteService.createFavourite(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favourites'] });
    },
  });

  const deleteFavouriteMutation = useMutation({
    mutationFn: (id: string) => favouriteService.deleteFavourite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favourites'] });
    },
  });

  // Filter projects to show only active (non-archived) projects
  const activeProjects = useMemo(() => {
    if (!projectsData) return [];
    return projectsData.filter((project) => project.status !== 'archived');
  }, [projectsData]);

  // Sort active projects alphabetically by name for the project combobox
  const sortedActiveProjects = useMemo(() => {
    return [...activeProjects].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [activeProjects]);

  // Label shown in the project combobox input, derived from form data so favourites
  // referencing archived projects (excluded from activeProjects) still display correctly
  const selectedProjectLabel = useMemo(() => {
    const { project_id, project_name, project_number } = formData.project_info;
    if (!project_id) return '';
    return `${project_name} (${project_number})`;
  }, [formData.project_info]);

  const filteredProjects = useMemo(() => {
    const search = projectSearch.trim().toLowerCase();
    if (!search || projectSearch === selectedProjectLabel) return sortedActiveProjects;

    return sortedActiveProjects.filter((project) => {
      const name = (project.name || '').toLowerCase();
      const number = (project.project_number || '').toLowerCase();
      return name.includes(search) || number.includes(search);
    });
  }, [projectSearch, selectedProjectLabel, sortedActiveProjects]);

  const projectCollection = useMemo(
    () =>
      createListCollection({
        items: filteredProjects,
        itemToValue: (project) => project.id.toString(),
        itemToString: (project) => `${project.name} (${project.project_number})`,
      }),
    [filteredProjects]
  );

  // Keep the combobox input in sync with programmatic project changes (favourites, edit mode, reset)
  useEffect(() => {
    setProjectSearch(selectedProjectLabel);
  }, [selectedProjectLabel]);

  // Get the selected project's type
  const selectedProjectType = useMemo(() => {
    if (!formData.project_info.project_id) return null;
    const project = activeProjects.find(
      (p) => p.id.toString() === formData.project_info.project_id
    );
    return project?.type || null;
  }, [formData.project_info.project_id, activeProjects]);

  // Filter WBS based on project type (comparing in lowercase)
  const filteredWbsData = useMemo(() => {
    if (!selectedProjectType || !wbsData) return wbsData || [];

    // Normalize project type to lowercase for comparison
    const normalizedProjectType = selectedProjectType.toLowerCase();

    return wbsData.filter((wbs: WBS) => {
      // Normalize WBS project_type to lowercase for comparison
      const normalizedWbsType = (wbs.project_type || 'internal').toLowerCase();
      return normalizedWbsType === normalizedProjectType;
    });
  }, [selectedProjectType, wbsData]);

  // Sort orders alphabetically by order_number
  const sortedOrdersData = useMemo(() => {
    if (!ordersData) return [];

    return [...ordersData].sort((a, b) => {
      const orderA = a.order_number?.toLowerCase() || '';
      const orderB = b.order_number?.toLowerCase() || '';
      return orderA.localeCompare(orderB);
    });
  }, [ordersData]);

  // Sort filtered WBS tasks alphabetically by task_code, excluding task codes '1', '2', and '3'
  const sortedWbsData = useMemo(() => {
    if (!filteredWbsData) return [];

    return [...filteredWbsData]
      .filter((wbs) => wbs.task_code !== '1' && wbs.task_code !== '2' && wbs.task_code !== '3')
      .sort((a, b) => {
        const taskA = a.task_code?.toLowerCase() || '';
        const taskB = b.task_code?.toLowerCase() || '';
        return taskA.localeCompare(taskB);
      });
  }, [filteredWbsData]);

  // Initialize form data when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (isEdit && entry) {
        const project_id = entry.project_info.project_id;
        setFormData({
          id: entry.id,
          date_of_year: selectedDate.toISOString(),
          project_info: entry.project_info,
          order_info: entry.order_info,
          task_info: entry.task_info,
          hours_logged: entry.hours_logged,
          description: entry.description,
          status: 'draft',
          work_type: entry.work_type || null,
          billable: entry.billable ?? entry.task_info?.billable ?? false,
        });
        // Reset prev project id ref when initializing edit mode
        prevProjectIdRef.current = project_id;
      } else {
        setFormData((prev) => ({
          ...prev,
          id: generateId(),
          project_info: {
            project_id: '',
            project_number: '',
            project_name: '',
          },
          order_info: {
            order_id: '',
            order_number: '',
            comments: '',
          },
          task_info: {
            task_id: '',
            task_code: '',
            task_name: '',
            billable: false,
          },
          hours_logged: 0,
          description: '',
          status: 'draft',
          work_type: null,
          billable: false,
        }));
        // Reset prev project id ref when initializing new entry
        prevProjectIdRef.current = '';
      }
      setErrors({});
    }
  }, [isOpen, isEdit, entry, employeeId]);

  // Reset order and task selection when project changes (only if switching from one project to another)
  useEffect(() => {
    const currentProjectId = formData.project_info.project_id;
    const prevProjectId = prevProjectIdRef.current;

    // Only reset if project actually changed from one non-empty value to another
    // Don't reset on initial load (when prevProjectId is empty and currentProjectId is set)
    if (prevProjectId !== '' && currentProjectId !== '' && prevProjectId !== currentProjectId) {
      updateOrderInfo({
        order_id: '',
        order_number: '',
        comments: '',
      });
      // Reset task when project changes (since task type must match project type)
      updateTaskInfo({
        task_id: '',
        task_code: '',
        task_name: '',
        billable: false,
      });
    }

    prevProjectIdRef.current = currentProjectId;
  }, [formData.project_info.project_id]);

  // Reset task if current task doesn't match new project type
  useEffect(() => {
    if (!selectedProjectType || !formData.task_info.task_id) return;

    const currentTask = wbsData?.find((t: WBS) => t.id.toString() === formData.task_info.task_id);
    if (currentTask) {
      const normalizedProjectType = selectedProjectType.toLowerCase();
      const normalizedTaskType = (currentTask.project_type || 'internal').toLowerCase();

      // If task type doesn't match project type, reset task
      if (normalizedTaskType !== normalizedProjectType) {
        updateTaskInfo({
          task_id: '',
          task_code: '',
          task_name: '',
          billable: false,
        });
      }
    }
  }, [selectedProjectType, wbsData, formData.task_info.task_id]);

  // Reset work_type when project type changes from external to non-external
  useEffect(() => {
    const currentProjectType = selectedProjectType?.toLowerCase() || null;
    const prevProjectType = prevProjectTypeRef.current;

    // Only reset if switching from external to non-external
    if (prevProjectType === 'external' && currentProjectType !== 'external' && formData.work_type) {
      handleInputChange('work_type', null);
      // Clear error if it exists
      if (errors.work_type) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.work_type;
          return newErrors;
        });
      }
    }

    prevProjectTypeRef.current = currentProjectType;
  }, [selectedProjectType, formData.work_type]);

  // Helper function to restore scroll
  const restoreScroll = () => {
    if (originalOverflowRef.current !== null) {
      if (originalOverflowRef.current) {
        document.body.style.overflow = originalOverflowRef.current;
      } else {
        document.body.style.removeProperty('overflow');
      }
      originalOverflowRef.current = null;
    } else {
      // Safety fallback: if ref is null but overflow is hidden, restore it
      if (document.body.style.overflow === 'hidden') {
        document.body.style.removeProperty('overflow');
      }
    }
  };

  // Wrapper for onClose that ensures scroll is restored
  const handleClose = () => {
    restoreScroll();
    onClose();
  };

  // Handle escape key to close dialog and prevent body scroll
  useEffect(() => {
    if (!isOpen) {
      // Restore scroll when dialog closes
      restoreScroll();
      return;
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    // Save original inline overflow value before modifying
    // Store in ref to persist across unmounts (unmountOnExit)
    originalOverflowRef.current = document.body.style.overflow || null;

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      // Restore scroll on cleanup
      restoreScroll();
    };
  }, [isOpen, onClose]);

  // Ensure scroll is restored on component unmount (critical for unmountOnExit)
  useEffect(() => {
    return () => {
      restoreScroll();
    };
  }, []);

  const handleInputChange = (
    field: string,
    value:
      | string
      | number
      | boolean
      | Project
      | Order
      | WBS
      | ProjectInfo
      | OrderInfo
      | TaskInfo
      | undefined
      | null
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const updateProjectInfo = (updates: Partial<ProjectInfo>) => {
    setFormData((prev) => ({
      ...prev,
      project_info: { ...prev.project_info, ...updates },
    }));
  };

  const updateOrderInfo = (updates: Partial<OrderInfo>) => {
    setFormData((prev) => ({
      ...prev,
      order_info: { ...prev.order_info, ...updates },
    }));
  };

  const updateTaskInfo = (updates: Partial<TaskInfo>) => {
    setFormData((prev) => ({
      ...prev,
      task_info: { ...prev.task_info, ...updates },
      billable: updates.billable !== undefined ? updates.billable : prev.billable,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.project_info.project_id || formData.project_info.project_id === '') {
      newErrors.project_id = 'Project is required';
    }
    // Order No is now optional - removed validation
    if (!formData.task_info.task_id || formData.task_info.task_id === '') {
      newErrors.task_id = 'Task is required';
    }
    if (formData.hours_logged && formData.hours_logged < 0) {
      newErrors.hours_worked = 'Hours cannot be negative';
    }
    // Work type is required when project type is external
    if (selectedProjectType?.toLowerCase() === 'external') {
      if (!formData.work_type || formData.work_type.trim() === '') {
        newErrors.work_type = 'Work type is required for external projects';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFavouriteSelect = (favouriteId: string) => {
    const favourite = favourites?.find((f) => f.id === favouriteId);
    if (!favourite) return;

    updateProjectInfo({
      project_id: favourite.project_info.project_id,
      project_number: favourite.project_info.project_number,
      project_name: favourite.project_info.project_name,
    });

    if (favourite.order_info.order_id) {
      updateOrderInfo({
        order_id: favourite.order_info.order_id,
        order_number: favourite.order_info.order_number,
        comments: favourite.order_info.comments,
      });
    } else {
      updateOrderInfo({
        order_id: '',
        order_number: '',
        comments: '',
      });
    }

    updateTaskInfo({
      task_id: favourite.task_info.task_id,
      task_code: favourite.task_info.task_code,
      task_name: favourite.task_info.task_name,
      billable: favourite.task_info.billable,
    });

    handleInputChange('work_type', favourite.worktype || null);

    if (favourite.is_project_archived) {
      setShowArchivedWarning(true);
    } else {
      setShowArchivedWarning(false);
    }

    setSelectedFavouriteId('');
  };

  const normalizeOrderId = (orderId: string | undefined | null): string => {
    if (!orderId || orderId === '' || orderId === '00000000-0000-0000-0000-000000000000') {
      return '';
    }
    return orderId;
  };

  const handleSaveFavourite = async () => {
    if (!formData.project_info.project_id || !formData.task_info.task_id) {
      setSubmitError('Project and Task are required to save as favourite');
      return;
    }

    // Check for duplicates before making API call (in case favourites haven't loaded yet)
    if (favourites && favourites.length > 0) {
      const normalizedFormOrderId = normalizeOrderId(formData.order_info.order_id);
      const normalizedFormWorkType = formData.work_type?.trim() || null;
      const isDuplicate = favourites.some((f) => {
        const normalizedFavouriteWorkType = f.worktype?.trim() || null;
        return (
          f.project_info.project_id === formData.project_info.project_id &&
          f.task_info.task_id === formData.task_info.task_id &&
          normalizeOrderId(f.order_info.order_id) === normalizedFormOrderId &&
          normalizedFavouriteWorkType === normalizedFormWorkType &&
          f.task_info.billable === formData.billable
        );
      });
      if (isDuplicate) {
        setSubmitError('This favourite already exists');
        return;
      }
    }

    setSubmitError(null);

    try {
      const favouriteData: CreateFavouriteRequest = {
        project_id: formData.project_info.project_id,
        task_id: formData.task_info.task_id,
        ...(formData.order_info.order_id && formData.order_info.order_id !== ''
          ? { order_id: formData.order_info.order_id }
          : {}),
        worktype:
          formData.work_type && formData.work_type.trim() !== '' ? formData.work_type.trim() : null,
        billable: formData.billable,
      };

      await createFavouriteMutation.mutateAsync(favouriteData);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to save favourite');
    }
  };

  const handleDeleteFavourite = async (favouriteId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to remove this favourite?')) {
      return;
    }

    setIsDeletingFavourite(favouriteId);
    try {
      await deleteFavouriteMutation.mutateAsync(favouriteId);
      if (selectedFavouriteId === favouriteId) {
        setSelectedFavouriteId('');
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to delete favourite');
    } finally {
      setIsDeletingFavourite(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Prepare data for API call
      const apiData: any = {
        employee_id: employeeId,
        project_id: formData.project_info.project_id,
        task_id: formData.task_info.task_id,
        date_of_year: selectedDate.toISOString(),
        hours_worked: formData.hours_logged,
        status: formData.status,
        billable: formData.billable,
      };

      // Only include order_id if it's provided
      if (formData.order_info.order_id && formData.order_info.order_id !== '') {
        apiData.order_id = formData.order_info.order_id;
      }

      // Only include description if it's provided
      if (formData.description && formData.description.trim() !== '') {
        apiData.description = formData.description.trim();
      }

      // Only include work_type if it's provided
      if (formData.work_type && formData.work_type.trim() !== '') {
        apiData.work_type = formData.work_type.trim();
      }

      // Call the API
      const response = isEdit
        ? await timeEntryService.updateProjectLoggedHours(formData.id, apiData)
        : await timeEntryService.createProjectLoggedHours(apiData);

      if (response.success) {
        // Convert response.data (EmployeeProjectHours) to ProjectLogEntry format
        const projectLogEntry: ProjectLogEntry = {
          id: formData.id,
          project_info: formData.project_info,
          order_info: formData.order_info,
          task_info: formData.task_info,
          hours_logged: formData.hours_logged,
          description: formData.description,
          work_type: formData.work_type,
          billable: formData.billable,
        };

        // Call the parent's onSave callback
        onSave(projectLogEntry);

        // Trigger refetch of backend data
        if (onEntrySaved) {
          onEntrySaved();
        }

        // Close the dialog
        onClose();
      } else {
        setSubmitError(response.message || 'Failed to save time entry');
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDuplicateFavourite = useMemo(() => {
    if (!favourites || !formData.project_info.project_id || !formData.task_info.task_id) {
      return false;
    }
    const normalizedFormOrderId = normalizeOrderId(formData.order_info.order_id);
    const normalizedFormWorkType = formData.work_type?.trim() || null;
    return favourites.some((f) => {
      const normalizedFavouriteWorkType = f.worktype?.trim() || null;
      return (
        f.project_info.project_id === formData.project_info.project_id &&
        f.task_info.task_id === formData.task_info.task_id &&
        normalizeOrderId(f.order_info.order_id) === normalizedFormOrderId &&
        normalizedFavouriteWorkType === normalizedFormWorkType &&
        f.task_info.billable === formData.billable
      );
    });
  }, [
    favourites,
    formData.project_info.project_id,
    formData.task_info.task_id,
    formData.order_info.order_id,
    formData.work_type,
    formData.billable,
  ]);

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(e) => {
        if (!e.open) {
          handleClose();
        }
      }}
      lazyMount
      unmountOnExit
    >
      <Portal>
        <Dialog.Backdrop
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 50,
          }}
        />
        <Dialog.Positioner
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: '1rem',
          }}
        >
          <Dialog.Content
            style={{
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              maxWidth: '56rem',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
            }}
          >
            <div style={dialogTheme.header}>
              <Dialog.Title style={dialogTheme.title}>
                {isEdit ? 'Edit Time Entry' : 'Add Time Entry'}
              </Dialog.Title>
              <Dialog.CloseTrigger
                style={dialogTheme.closeButton}
                onMouseEnter={(e) =>
                  Object.assign(e.currentTarget.style, dialogTheme.closeButtonHover)
                }
                onMouseLeave={(e) =>
                  Object.assign(e.currentTarget.style, { backgroundColor: 'transparent' })
                }
              >
                <XIcon size={20} />
              </Dialog.CloseTrigger>
            </div>

            <Dialog.Description
              style={{
                padding: '0 1rem 0.75rem',
                color: '#6b7280',
                fontSize: '0.8125rem',
                margin: 0,
              }}
            >
              {isEdit
                ? 'Update the time entry details below.'
                : 'Fill in the details for the new time entry.'}
            </Dialog.Description>

            <form onSubmit={handleSubmit} style={{ padding: '1rem 2rem 2rem' }}>
              {/* Bridge Day Warning */}
              {isBridge && (
                <div
                  style={{
                    padding: '0.75rem',
                    backgroundColor: '#fef3c7',
                    border: '1px solid #fbbf24',
                    borderRadius: '0.375rem',
                    color: '#92400e',
                    fontSize: '0.8125rem',
                    marginBottom: '1rem',
                  }}
                >
                  <span>Project hours entry is unavailable for Bridge Day.</span>
                </div>
              )}
              {/* Archived Project Warning */}
              {showArchivedWarning && (
                <div
                  style={{
                    padding: '0.75rem',
                    backgroundColor: '#fef3c7',
                    border: '1px solid #fbbf24',
                    borderRadius: '0.375rem',
                    color: '#92400e',
                    fontSize: '0.8125rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem',
                  }}
                >
                  <span>Warning: This favourite uses an archived project.</span>
                  <button
                    type="button"
                    onClick={() => setShowArchivedWarning(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#92400e',
                      cursor: 'pointer',
                      fontSize: '1.25rem',
                      padding: '0 0.5rem',
                    }}
                  >
                    ×
                  </button>
                </div>
              )}

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 3fr',
                  gap: '0',
                  alignItems: 'flex-start',
                }}
              >
                {/* Left Column - Form Fields */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem',
                    borderRight: '1px solid #e5e7eb',
                    paddingRight: '1.5rem',
                    minWidth: '300px',
                  }}
                >
                  {/* Project Field */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '0.8125rem',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '0.375rem',
                      }}
                    >
                      Project *
                    </label>
                    <Combobox.Root
                      collection={projectCollection}
                      value={
                        formData.project_info.project_id ? [formData.project_info.project_id] : []
                      }
                      inputValue={projectSearch}
                      disabled={isLoadingProjects || isBridge}
                      openOnClick
                      allowCustomValue={false}
                      positioning={{ sameWidth: true }}
                      onInputValueChange={(details) => setProjectSearch(details.inputValue)}
                      onOpenChange={(details) => {
                        if (!details.open) {
                          setProjectSearch(selectedProjectLabel);
                        }
                      }}
                      onValueChange={(details) => {
                        const selectedProjectId = details.value[0] || '';
                        const selectedProject = sortedActiveProjects.find(
                          (p) => p.id.toString() === selectedProjectId
                        );

                        updateProjectInfo({
                          project_id: selectedProjectId,
                          project_number: selectedProject?.project_number || '',
                          project_name: selectedProject?.name || '',
                        });
                        if (errors.project_id) {
                          setErrors((prev) => ({ ...prev, project_id: '' }));
                        }
                      }}
                    >
                      <Combobox.Control style={{ position: 'relative' }}>
                        <Combobox.Input
                          placeholder={
                            isLoadingProjects ? 'Loading projects...' : 'Search or select a project'
                          }
                          style={{
                            width: '100%',
                            padding: '0.625rem',
                            paddingRight: '2rem',
                            border: `1px solid ${errors.project_id ? '#ef4444' : '#d1d5db'}`,
                            borderRadius: '0.375rem',
                            fontSize: '0.8125rem',
                            outline: 'none',
                            transition: 'border-color 0.2s',
                            backgroundColor: 'white',
                            boxSizing: 'border-box',
                          }}
                          onFocus={(e) => (e.target.style.borderColor = '#3b82f6')}
                          onBlur={(e) =>
                            (e.target.style.borderColor = errors.project_id ? '#ef4444' : '#d1d5db')
                          }
                        />
                        <Combobox.Trigger
                          style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 0.5rem',
                            background: 'none',
                            border: 'none',
                            color: '#6b7280',
                            cursor: isLoadingProjects || isBridge ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <ChevronsUpDownIcon size={16} />
                        </Combobox.Trigger>
                      </Combobox.Control>
                      <Portal>
                        <Combobox.Positioner style={{ zIndex: 100 }}>
                          <Combobox.Content
                            style={{
                              backgroundColor: 'white',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                              maxHeight: '15rem',
                              overflowY: 'auto',
                              padding: '0.25rem',
                              fontSize: '0.8125rem',
                              outline: 'none',
                            }}
                          >
                            <Combobox.Empty
                              style={{ padding: '0.5rem', color: '#6b7280', fontSize: '0.8125rem' }}
                            >
                              No projects found
                            </Combobox.Empty>
                            {projectCollection.items.map((project) => (
                              <Combobox.Item
                                key={project.id}
                                item={project}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: '0.5rem',
                                  padding: '0.375rem 0.5rem',
                                  borderRadius: '0.25rem',
                                  color: '#374151',
                                  cursor: 'pointer',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#f9fafb';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                <Combobox.ItemText>
                                  {project.name} ({project.project_number})
                                </Combobox.ItemText>
                                <Combobox.ItemIndicator>
                                  <CheckIcon size={14} />
                                </Combobox.ItemIndicator>
                              </Combobox.Item>
                            ))}
                          </Combobox.Content>
                        </Combobox.Positioner>
                      </Portal>
                    </Combobox.Root>
                    {errors.project_id && (
                      <p style={{ color: '#ef4444', fontSize: '0.6875rem', marginTop: '0.25rem' }}>
                        {errors.project_id}
                      </p>
                    )}
                    {projectsError && (
                      <p style={{ color: '#ef4444', fontSize: '0.6875rem', marginTop: '0.25rem' }}>
                        Failed to load projects
                      </p>
                    )}
                  </div>

                  {/* Order No Field */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '0.8125rem',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '0.375rem',
                      }}
                    >
                      Order No
                    </label>
                    <select
                      value={formData.order_info.order_id || ''}
                      onChange={(e) => {
                        const selectedOrderId = e.target.value;
                        const selectedOrder = sortedOrdersData.find(
                          (o: Order) => o.id.toString() === e.target.value
                        );

                        updateOrderInfo({
                          order_id: selectedOrderId || '',
                          order_number: selectedOrder?.order_number || '',
                          comments: selectedOrder?.comments || '',
                        });
                        // Clear error when user starts typing
                        if (errors.order_id) {
                          setErrors((prev) => ({ ...prev, order_id: '' }));
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '0.625rem',
                        border: `1px solid ${errors.order_id ? '#ef4444' : '#d1d5db'}`,
                        borderRadius: '0.375rem',
                        fontSize: '0.8125rem',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                        backgroundColor: 'white',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = '#3b82f6')}
                      onBlur={(e) =>
                        (e.target.style.borderColor = errors.order_id ? '#ef4444' : '#d1d5db')
                      }
                      disabled={isLoadingOrders || formData.project_info.project_id === ''}
                    >
                      <option value="">
                        {formData.project_info.project_id === ''
                          ? 'Select a project first'
                          : isLoadingOrders
                            ? 'Loading orders...'
                            : 'Select an order (optional)'}
                      </option>
                      {sortedOrdersData.map((order: Order) => (
                        <option key={order.id} value={order.id.toString()}>
                          {order.order_number} - {order.comments || 'No description'}
                        </option>
                      ))}
                    </select>
                    {errors.order_id && (
                      <p style={{ color: '#ef4444', fontSize: '0.6875rem', marginTop: '0.25rem' }}>
                        {errors.order_id}
                      </p>
                    )}
                    {ordersError && (
                      <p style={{ color: '#ef4444', fontSize: '0.6875rem', marginTop: '0.25rem' }}>
                        Failed to load orders
                      </p>
                    )}
                    {formData.project_info.project_id === '' && (
                      <p style={{ color: '#6b7280', fontSize: '0.6875rem', marginTop: '0.25rem' }}>
                        Please select a project first to load orders
                      </p>
                    )}
                  </div>

                  {/* Task Field */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '0.8125rem',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '0.375rem',
                      }}
                    >
                      Task *
                    </label>
                    <select
                      value={formData.task_info.task_id || ''}
                      onChange={(e) => {
                        const selectedTaskId = e.target.value;
                        const selectedTask = sortedWbsData.find(
                          (t: WBS) => t.id.toString() === e.target.value
                        );

                        updateTaskInfo({
                          task_id: selectedTaskId || '',
                          task_code: selectedTask?.task_code || '',
                          task_name: selectedTask?.name || '',
                          billable: selectedTask?.billable || false,
                        });
                        // Clear error when user starts typing
                        if (errors.task_id) {
                          setErrors((prev) => ({ ...prev, task_id: '' }));
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '0.625rem',
                        border: `1px solid ${errors.task_id ? '#ef4444' : '#d1d5db'}`,
                        borderRadius: '0.375rem',
                        fontSize: '0.8125rem',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                        backgroundColor: 'white',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = '#3b82f6')}
                      onBlur={(e) =>
                        (e.target.style.borderColor = errors.task_id ? '#ef4444' : '#d1d5db')
                      }
                      disabled={isLoadingWbs || formData.project_info.project_id === '' || isBridge}
                    >
                      <option value="">
                        {formData.project_info.project_id === ''
                          ? 'Select a project first'
                          : isLoadingWbs
                            ? 'Loading tasks...'
                            : sortedWbsData.length === 0
                              ? `No ${selectedProjectType?.toLowerCase() || ''} tasks available`
                              : 'Select a task'}
                      </option>
                      {sortedWbsData
                        .filter((task: WBS) => task.task_code !== '002')
                        .map((task: WBS) => (
                          <option key={task.id} value={task.id.toString()}>
                            {task.task_code} - {task.name}{' '}
                            {task.billable ? '' : '(ONLY Non-Billable)'}
                          </option>
                        ))}
                    </select>
                    {errors.task_id && (
                      <p style={{ color: '#ef4444', fontSize: '0.6875rem', marginTop: '0.25rem' }}>
                        {errors.task_id}
                      </p>
                    )}
                    {wbsError && (
                      <p style={{ color: '#ef4444', fontSize: '0.6875rem', marginTop: '0.25rem' }}>
                        Failed to load tasks
                      </p>
                    )}
                    {formData.project_info.project_id === '' && (
                      <p style={{ color: '#6b7280', fontSize: '0.6875rem', marginTop: '0.25rem' }}>
                        Please select a project first to load tasks
                      </p>
                    )}
                    {/* Billable */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                      }}
                    >
                      <input
                        disabled={!formData.task_info.billable}
                        type="checkbox"
                        id="billable"
                        checked={formData.billable}
                        onChange={(e) => handleInputChange('billable', e.target.checked)}
                        style={{ width: '1rem', height: '1rem', accentColor: '#3b82f6' }}
                      />
                      <label
                        htmlFor="billable"
                        style={{
                          fontSize: '0.8125rem',
                          fontWeight: '500',
                          color: '#374151',
                          cursor: 'pointer',
                          opacity: formData.task_info.billable ? 1 : 0.5,
                        }}
                      >
                        Billable
                      </label>
                    </div>
                  </div>

                  {/* Work Type Field - Only show for external projects */}
                  {selectedProjectType?.toLowerCase() === 'external' && (
                    <div>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '0.8125rem',
                          fontWeight: '500',
                          color: '#374151',
                          marginBottom: '0.375rem',
                        }}
                      >
                        Work Type *
                      </label>
                      <select
                        value={formData.work_type || ''}
                        onChange={(e) => {
                          handleInputChange('work_type', e.target.value || null);
                          // Clear error when user starts typing
                          if (errors.work_type) {
                            setErrors((prev) => ({ ...prev, work_type: '' }));
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '0.625rem',
                          border: `1px solid ${errors.work_type ? '#ef4444' : '#d1d5db'}`,
                          borderRadius: '0.375rem',
                          fontSize: '0.8125rem',
                          outline: 'none',
                          transition: 'border-color 0.2s',
                          backgroundColor: 'white',
                        }}
                        onFocus={(e) => (e.target.style.borderColor = '#3b82f6')}
                        onBlur={(e) =>
                          (e.target.style.borderColor = errors.work_type ? '#ef4444' : '#d1d5db')
                        }
                        disabled={isBridge}
                      >
                        <option value="">Select work type</option>
                        <option value="office">Office</option>
                        <option value="site">Site</option>
                      </select>
                      {errors.work_type && (
                        <p
                          style={{ color: '#ef4444', fontSize: '0.6875rem', marginTop: '0.25rem' }}
                        >
                          {errors.work_type}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Description Field */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '0.8125rem',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '0.375rem',
                      }}
                    >
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={2}
                      style={{
                        width: '100%',
                        padding: '0.625rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem',
                        fontSize: '0.8125rem',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                        resize: 'vertical',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = '#3b82f6')}
                      onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
                      placeholder="Enter additional details (optional)"
                      disabled={isBridge}
                    />
                  </div>

                  {/* Hours Field */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '0.8125rem',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '0.375rem',
                      }}
                    >
                      Hours *
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.hours_logged || 0}
                      onChange={(e) => {
                        const value = Number.parseFloat(e.target.value) || 0;
                        handleInputChange('hours_logged', value);
                        // Clear error when user starts typing
                        if (errors.hours_worked) {
                          setErrors((prev) => ({ ...prev, hours_worked: '' }));
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '0.625rem',
                        border: `1px solid ${errors.hours_worked ? '#ef4444' : '#d1d5db'}`,
                        borderRadius: '0.375rem',
                        fontSize: '0.8125rem',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = '#3b82f6')}
                      onBlur={(e) =>
                        (e.target.style.borderColor = errors.hours_worked ? '#ef4444' : '#d1d5db')
                      }
                      placeholder="0.00"
                      disabled={isBridge}
                    />
                    {errors.hours_worked && (
                      <p style={{ color: '#ef4444', fontSize: '0.6875rem', marginTop: '0.25rem' }}>
                        {errors.hours_worked}
                      </p>
                    )}
                  </div>

                  {/* Error Display */}
                  {submitError && (
                    <div
                      style={{
                        marginTop: '0.75rem',
                        padding: '0.625rem',
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '0.375rem',
                        color: '#dc2626',
                        fontSize: '0.8125rem',
                      }}
                    >
                      {submitError}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: '0.5rem',
                      marginTop: '1rem',
                      paddingTop: '0.75rem',
                      borderTop: '1px solid #e5e7eb',
                    }}
                  >
                    <button
                      type="button"
                      onClick={handleClose}
                      style={mergeStyles(buttonTheme.base, buttonTheme.secondary, {
                        maxHeight: '2.5rem',
                        height: '2.5rem',
                      })}
                      onMouseEnter={(e) =>
                        Object.assign(e.currentTarget.style, buttonTheme.secondaryHover)
                      }
                      onMouseLeave={(e) =>
                        Object.assign(
                          e.currentTarget.style,
                          mergeStyles(buttonTheme.base, buttonTheme.secondary, {
                            maxHeight: '2.5rem',
                            height: '2.5rem',
                          })
                        )
                      }
                      onMouseDown={(e) =>
                        Object.assign(e.currentTarget.style, buttonTheme.secondaryActive)
                      }
                      onMouseUp={(e) =>
                        Object.assign(e.currentTarget.style, buttonTheme.secondaryHover)
                      }
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || isBridge}
                      style={mergeStyles(
                        buttonTheme.base,
                        buttonTheme.primary,
                        isSubmitting ? buttonTheme.primaryDisabled : {},
                        {
                          maxHeight: '2.5rem',
                          height: '2.5rem',
                          lineHeight: '1.2',
                          padding: '0.5rem 1rem',
                        }
                      )}
                      onMouseEnter={(e) => {
                        if (!isSubmitting) {
                          Object.assign(e.currentTarget.style, buttonTheme.primaryHover);
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSubmitting) {
                          Object.assign(
                            e.currentTarget.style,
                            mergeStyles(buttonTheme.base, buttonTheme.primary, {
                              maxHeight: '2.5rem',
                              height: '2.5rem',
                              lineHeight: '1.2',
                              padding: '0.5rem 1rem',
                            })
                          );
                        }
                      }}
                      onMouseDown={(e) => {
                        if (!isSubmitting) {
                          Object.assign(e.currentTarget.style, buttonTheme.primaryActive);
                        }
                      }}
                      onMouseUp={(e) => {
                        if (!isSubmitting) {
                          Object.assign(e.currentTarget.style, buttonTheme.primaryHover);
                        }
                      }}
                    >
                      {isSubmitting ? 'Saving...' : isEdit ? 'Update Entry' : 'Add Entry'}
                    </button>
                  </div>
                </div>

                {/* Right Column - Hours Info, Favourites */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem',
                    paddingTop: '1rem',
                    paddingLeft: '1.5rem',
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  {/* Hours Information */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gridTemplateRows: 'auto auto',
                      gap: '0.5rem',
                      marginBottom: '1.5rem',
                    }}
                  >
                    <div
                      style={{
                        padding: '0.5rem',
                        backgroundColor: 'white',
                        borderRadius: '0.25rem',
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '0.6875rem',
                          color: '#6b7280',
                          marginBottom: '0.125rem',
                          fontWeight: '500',
                        }}
                      >
                        Planned Hours
                      </div>
                      <div
                        style={{
                          fontSize: '1rem',
                          color: '#1f2937',
                          fontWeight: '600',
                        }}
                      >
                        {plannedHours?.toFixed(2) ?? '0.00'}h
                      </div>
                    </div>

                    <div
                      style={{
                        padding: '0.5rem',
                        backgroundColor: 'white',
                        borderRadius: '0.25rem',
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '0.6875rem',
                          color: '#6b7280',
                          marginBottom: '0.125rem',
                          fontWeight: '500',
                        }}
                      >
                        Actual Hours
                      </div>
                      <div
                        style={{
                          fontSize: '1rem',
                          color: '#1f2937',
                          fontWeight: '600',
                        }}
                      >
                        {actualHours?.toFixed(2) ?? '0.00'}h
                      </div>
                    </div>
                    <div
                      style={{
                        padding: '0.5rem',
                        backgroundColor: 'white',
                        borderRadius: '0.25rem',
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '0.6875rem',
                          color: '#6b7280',
                          marginBottom: '0.125rem',
                          fontWeight: '500',
                        }}
                      >
                        Total Project Hours
                      </div>
                      <div
                        style={{
                          fontSize: '1rem',
                          color: '#1f2937',
                          fontWeight: '600',
                        }}
                      >
                        {(totalProjectHours ?? 0).toFixed(2)}h
                      </div>
                    </div>
                    <div
                      style={{
                        gridColumn: '1 / 4',
                        padding: '0.5rem',
                        backgroundColor: 'white',
                        borderRadius: '0.25rem',
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '0.6875rem',
                          color: '#6b7280',
                          marginBottom: '0.125rem',
                          fontWeight: '500',
                        }}
                      >
                        Remaining Hours
                      </div>
                      <div
                        style={{
                          fontSize: '1rem',
                          color:
                            (actualHours ?? 0) - (totalProjectHours ?? 0) < 0
                              ? '#ef4444'
                              : '#1f2937',
                          fontWeight: '600',
                        }}
                      >
                        {((actualHours ?? 0) - (totalProjectHours ?? 0)).toFixed(2)}h
                      </div>
                    </div>
                  </div>

                  {/* Save Favourite Button */}
                  {formData.project_info.project_id && formData.task_info.task_id && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={handleSaveFavourite}
                        disabled={
                          createFavouriteMutation.isPending ||
                          isDuplicateFavourite ||
                          isLoadingFavourites
                        }
                        style={mergeStyles(
                          buttonTheme.base,
                          {
                            backgroundColor: isDuplicateFavourite ? '#9ca3af' : '#10b981',
                            color: 'white',
                            opacity:
                              createFavouriteMutation.isPending || isDuplicateFavourite ? 0.6 : 1,
                            cursor:
                              createFavouriteMutation.isPending || isDuplicateFavourite
                                ? 'not-allowed'
                                : 'pointer',
                            fontSize: '0.75rem',
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            lineHeight: '1.2',
                            padding: '0.5rem 0.75rem',
                            maxWidth: '200px',
                            textAlign: 'left',
                          },
                          createFavouriteMutation.isPending ? buttonTheme.primaryDisabled : {}
                        )}
                        title={
                          isDuplicateFavourite
                            ? 'This favourite already exists'
                            : 'Save current selection as favourite'
                        }
                        onMouseEnter={(e) => {
                          if (!createFavouriteMutation.isPending && !isDuplicateFavourite) {
                            e.currentTarget.style.backgroundColor = '#059669';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!createFavouriteMutation.isPending && !isDuplicateFavourite) {
                            e.currentTarget.style.backgroundColor = '#10b981';
                          }
                        }}
                      >
                        {createFavouriteMutation.isPending ? (
                          'Saving...'
                        ) : (
                          <>
                            <span
                              style={{
                                display: 'inline-block',
                                marginRight: '0.1rem',
                                fontSize: '1.2rem',
                              }}
                            >
                              ⭐
                            </span>
                            <span>Favourite Current Selection</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Favourites Dropdown - Only show when creating new entry */}

                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '0.8125rem',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '0.375rem',
                      }}
                    >
                      Favourite List
                    </label>
                    <div
                      style={{
                        position: 'relative',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem',
                        backgroundColor: 'white',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        width: '100%',
                      }}
                    >
                      {isLoadingFavourites ? (
                        <div
                          style={{
                            padding: '0.625rem',
                            color: '#6b7280',
                            fontSize: '0.8125rem',
                            textAlign: 'center',
                          }}
                        >
                          Loading favourites...
                        </div>
                      ) : favourites && favourites.length > 0 ? (
                        <table
                          style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '0.75rem',
                            tableLayout: 'fixed',
                          }}
                        >
                          <thead>
                            <tr
                              style={{
                                backgroundColor: '#f9fafb',
                                borderBottom: '2px solid #e5e7eb',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                              }}
                            >
                              <th
                                style={{
                                  padding: '0.375rem 0.25rem',
                                  textAlign: 'left',
                                  fontWeight: '600',
                                  color: '#374151',
                                  fontSize: '0.6875rem',
                                  textTransform: 'uppercase',
                                  width: '26%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Project
                              </th>
                              <th
                                style={{
                                  padding: '0.375rem 0.25rem',
                                  textAlign: 'left',
                                  fontWeight: '600',
                                  color: '#374151',
                                  fontSize: '0.6875rem',
                                  textTransform: 'uppercase',
                                  width: '18%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Order
                              </th>
                              <th
                                style={{
                                  padding: '0.375rem 0.25rem',
                                  textAlign: 'left',
                                  fontWeight: '600',
                                  color: '#374151',
                                  fontSize: '0.6875rem',
                                  textTransform: 'uppercase',
                                  width: '26%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Task
                              </th>
                              <th
                                style={{
                                  padding: '0.375rem 0.25rem',
                                  textAlign: 'left',
                                  fontWeight: '600',
                                  color: '#374151',
                                  fontSize: '0.6875rem',
                                  textTransform: 'uppercase',
                                  width: '10%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Billable
                              </th>
                              <th
                                style={{
                                  padding: '0.375rem 0.25rem',
                                  textAlign: 'left',
                                  fontWeight: '600',
                                  color: '#374151',
                                  fontSize: '0.6875rem',
                                  textTransform: 'uppercase',
                                  width: '10%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Work Type
                              </th>
                              <th
                                style={{
                                  padding: '0.375rem 0.25rem',
                                  width: '10%',
                                  minWidth: '30px',
                                }}
                              />
                            </tr>
                          </thead>
                          <tbody>
                            {favourites.map((favourite) => (
                              <tr
                                key={favourite.id}
                                style={{
                                  borderBottom: '1px solid #f3f4f6',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#f9fafb';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                                onClick={() => handleFavouriteSelect(favourite.id)}
                              >
                                <td
                                  style={{
                                    padding: '0.375rem 0.25rem',
                                    color: favourite.is_project_archived ? '#f59e0b' : '#374151',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  <div
                                    style={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                    title={`${favourite.project_info.project_number} ${favourite.project_info.project_name}`}
                                  >
                                    {favourite.is_project_archived && (
                                      <span
                                        style={{
                                          color: '#f59e0b',
                                          fontSize: '0.75rem',
                                          marginRight: '0.25rem',
                                        }}
                                      >
                                        [Archived]{' '}
                                      </span>
                                    )}
                                    <span style={{ fontWeight: '500' }}>
                                      {favourite.project_info.project_number}
                                    </span>{' '}
                                    {favourite.project_info.project_name}
                                  </div>
                                </td>
                                <td
                                  style={{
                                    padding: '0.375rem 0.25rem',
                                    color: '#6b7280',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {favourite.order_info.order_number ? (
                                    <div
                                      style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                      title={`${favourite.order_info.order_number}${favourite.order_info.comments ? ` ${favourite.order_info.comments}` : ''}`}
                                    >
                                      {favourite.order_info.order_number}
                                      {favourite.order_info.comments && (
                                        <> {favourite.order_info.comments}</>
                                      )}
                                    </div>
                                  ) : (
                                    <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>-</span>
                                  )}
                                </td>
                                <td
                                  style={{
                                    padding: '0.375rem 0.25rem',
                                    color: '#374151',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  <div
                                    style={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                    title={`${favourite.task_info.task_code} ${favourite.task_info.task_name}`}
                                  >
                                    <span style={{ fontWeight: '500' }}>
                                      {favourite.task_info.task_code}
                                    </span>{' '}
                                    {favourite.task_info.task_name}
                                  </div>
                                </td>
                                <td
                                  style={{
                                    padding: '0.375rem 0.25rem',
                                    color: '#374151',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {favourite.task_info.billable ? '✓' : ''}
                                </td>
                                <td
                                  style={{
                                    padding: '0.375rem 0.25rem',
                                    color: '#374151',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {favourite.worktype ? (
                                    <div
                                      style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        textTransform: 'capitalize',
                                      }}
                                      title={favourite.worktype}
                                    >
                                      {favourite.worktype}
                                    </div>
                                  ) : (
                                    <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>-</span>
                                  )}
                                </td>
                                <td
                                  style={{
                                    padding: '0.375rem 0.25rem',
                                    textAlign: 'center',
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    type="button"
                                    onClick={(e) => handleDeleteFavourite(favourite.id, e)}
                                    disabled={isDeletingFavourite === favourite.id}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      color: '#ef4444',
                                      padding: '0.25rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      opacity: isDeletingFavourite === favourite.id ? 0.5 : 1,
                                    }}
                                    title="Delete favourite"
                                  >
                                    <Trash2Icon size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div
                          style={{
                            padding: '0.625rem',
                            color: '#6b7280',
                            fontSize: '0.8125rem',
                            textAlign: 'center',
                          }}
                        >
                          No favourites available
                        </div>
                      )}
                    </div>
                    {favouritesError && (
                      <p style={{ color: '#ef4444', fontSize: '0.6875rem', marginTop: '0.25rem' }}>
                        Failed to load favourites
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
